import React, {useEffect, useState} from "react";
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography
} from "@mui/material";
import {ErrorOutline as ErrorIcon, InfoOutlined as InfoIcon} from "@mui/icons-material";
import {pendenciasPorGrau, plural} from "../sistemaPagina";
import {getBackendEntityMessage} from "../sistemasUtils";

const SEM_PERMISSAO = 'Pôr à venda pede a funcionalidade "Pôr sistema à venda" no seu perfil. Peça o acesso a quem administra o backoffice.';

const Lista = ({itens, icone}) => (
    <Stack component="ul" spacing={1} sx={{listStyle: 'none', p: 0, m: 0}}>
        {itens.map((item) => (
            <Stack component="li" key={`${item.codigo}-${item.alvo?.id}`} direction="row" spacing={1} alignItems="flex-start">
                {icone}
                <Typography variant="body2">{item.mensagem}</Typography>
            </Stack>
        ))}
    </Stack>
);

/*
 * "Pôr à venda" (BBF-173): em desenvolvimento → à venda. Com pendência que bloqueia, mostra a lista e não
 * pede confirmação. Se a API recusar mesmo assim (409, outra pessoa mudou o catálogo), a lista da recusa
 * substitui a da tela.
 */
export default function PorAVendaDialog({aberto, sistema, pendencias, carregando, onPorAVenda, onVerPendencias, onFechar}) {
    const [recusa, setRecusa] = useState(null);
    const [erro, setErro] = useState('');
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        if (aberto) {
            setRecusa(null);
            setErro('');
        }
    }, [aberto]);

    const porGrau = pendenciasPorGrau(recusa || pendencias);
    const recusado = !carregando && porGrau.bloqueia.length > 0;
    const nome = sistema?.nome || 'O sistema';

    const confirmar = async () => {
        setSalvando(true);
        setErro('');
        try {
            await onPorAVenda();
        } catch (error) {
            const status = error?.response?.status;
            if (status === 409 && error.response.data?.valor?.itens) setRecusa(error.response.data.valor);
            else setErro(status === 403 ? SEM_PERMISSAO : getBackendEntityMessage(error, 'Não foi possível pôr o sistema à venda.'));
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Dialog open={aberto} onClose={onFechar} fullWidth maxWidth="sm">
            <DialogTitle>{recusado ? `${nome} ainda não pode ir à venda` : `Pôr ${nome} à venda?`}</DialogTitle>
            <DialogContent dividers>
                {carregando ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={18} />
                        <Typography color="text.secondary">Conferindo as pendências…</Typography>
                    </Stack>
                ) : (
                    <Stack spacing={2}>
                        {recusado ? (
                            <>
                                <Typography>
                                    {plural(porGrau.bloqueia.length, 'pendência impede', 'pendências impedem')} pôr o sistema à venda. Nada disso impede salvar: resolva cada uma e tente de novo.
                                </Typography>
                                <Lista itens={porGrau.bloqueia} icone={<ErrorIcon fontSize="small" color="error" />} />
                            </>
                        ) : (
                            <Typography>
                                O sistema sai de desenvolvimento, e os planos dele passam a poder ser publicados. Depois de à venda, ele não volta a desenvolvimento.
                            </Typography>
                        )}
                        {porGrau.construcao.length ? (
                            <Alert severity="warning" variant="outlined">
                                {plural(porGrau.construcao.length, 'item segue', 'itens seguem')} em construção e não {porGrau.construcao.length === 1 ? 'impede' : 'impedem'}: {porGrau.construcao.length === 1 ? 'fica' : 'ficam'} fora do direito até {porGrau.construcao.length === 1 ? 'ficar completo' : 'ficarem completos'}.
                            </Alert>
                        ) : null}
                        {!recusado && porGrau.aviso.length ? (
                            <Stack spacing={1}>
                                <Typography variant="caption" color="text.secondary">Confira antes</Typography>
                                <Lista itens={porGrau.aviso} icone={<InfoIcon fontSize="small" color="info" />} />
                            </Stack>
                        ) : null}
                        {erro ? <Alert severity="error">{erro}</Alert> : null}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                {recusado ? (
                    <>
                        <Button onClick={onFechar}>Fechar</Button>
                        <Button variant="contained" onClick={onVerPendencias}>Ver as pendências</Button>
                    </>
                ) : (
                    <>
                        <Button onClick={onFechar}>Cancelar</Button>
                        <Button variant="contained" onClick={confirmar} disabled={carregando || salvando}>Pôr à venda</Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}
